import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import classnames from 'classnames';
import i18n from 'util/i18n';
import * as formTypes from 'constants/formTypes';
import { pvqReviewSections, reviewSections } from 'config/formTypes';
import { getSectionKeys } from '@redux/modules/section/selectors';
import { nestedFormSectionsSelector } from 'selectors/navigation';
import Logout from 'components/Navigation/Logout';
import StickyHeader from 'components/Sticky/StickyHeader';
import KickbackBanner from 'components/KickbackBanner';
import { STATUS_SUBMITTED } from 'constants/enums/applicationStatuses';
import Introduction from 'components/Form/Introduction';
import { NavigationToggle, Navigation } from 'components/Navigation';
import { SectionTitle } from 'components/SectionTitle';
import { ProgressBar } from 'components/ProgressBar';
import Survey from 'components/Form/Survey';
import ScoreCard from 'components/ScoreCard/ScoreCard';
import { AppKeydownWatchers, FormDetails } from 'util/singletons';
import { renewSessionRequest } from '@redux/modules/authentication/actions';
import LinkButton from 'components/Form/LinkButton';
import KickbackFailureWarning from 'components/Form/KickbackFailureWarning';
import CreditFreezeBanner from 'components/CreditFreezeBanner';
import { isPVQForm, shouldDisplayCreditFreeze } from 'helpers/form';
import { Form } from '../../views';

/*
           1/6-ish                                 2/3-ish                               1/6-ish
  ------------------------------------------------------------------------------------------------
  |                       |
  | Title                 | <Page title />
  | <Logout button />     |
  |                       |
  ------------------------------------------------------------------------------------------------
  |                       |
  | <Score />             | <View />
  | <Nav />               |  - This will be where the main content lies
  |                       |
  |                       |
  |                       |
  |                       |
  |                       |
  ------------------------------------------------------------------------------------------------
  |
  | <Footer />
  |
  ------------------------------------------------------------------------------------------------
*/

class App extends React.Component {
  refreshTimer = 0;

  constructor(props) {
    super(props);

    this.submitted = this.props.settings.status === STATUS_SUBMITTED;

    this.sectionFocusEl = React.createRef();
    this.keepSessionAlive = this.keepSessionAlive.bind(this);
    this.onKickbackWarningDismiss = this.onKickbackWarningCheck.bind(this);
    this.interval = 60000; // tick every minute
    this.kickbackWarningHasBeenDisabled = false;
  }

  componentDidMount() {
    AppKeydownWatchers.getInstance().watchErrorDebugger(true);
    AppKeydownWatchers.getInstance().watchUserActivity(true);
    /*
      This timer will run every minute for the duration of the user using the application. In addition refreshing will occur when the user saves.
     In any given minute where user activity occurs (typeing or clicking) the session will be refreshed.
     This refresh action will occur within one minute of the user's manual action
     If the session timeout is nearly up the modal will pop up to have the user manually push the button.
     These session refreshes are separate from save events which occur regularly and do not require a
     session refresh upon each action.

     This particular model will not allow the API to have more than SessionTimeout between client interactions;
     however, some client interation may be defferred by up to a minute. This spares client and server processing by
     using each minute to batch refreshes resulting in clients having no more than one refresh per minute. This
     monitors mouse click interaction and keyboard button presses.

     The purpose of this is a person who is activly typing or clicking in any section, reviewing, inputting or
     otherwise interacting should not be interrupted with timeout notices. A user who remains idle and waits until
     the final minute before the timeout is displayed to interact with the screen may still be presented with the continue
     session timeout modal, but should have all data auto saved. If the user's last action was within the final minute of the timeout This will fully
     prevent an active user who is using the system encountering an unexpected timeout.
    */
    this.refreshTimer = window.setInterval(this.keepSessionAlive, this.interval);
  }

  componentDidUpdate(prevProps) {
    const { location } = this.props;

    /**
     * for keyboard navigation accessbility, focus on the main content area after a new section is
     * navigated to */
    if (location.pathname !== prevProps.location.pathname && this.sectionFocusEl?.current) {
      this.sectionFocusEl.current.focus();
    }
    this.submitted = this.props.settings.status === STATUS_SUBMITTED;

    if (!this.kickbackWarningHasBeenDisabled && this.props.application?.Citizenship?.Status?.CitizenshipStatus?.value) {
      this.onKickbackWarningCheck();
    }
  }

  componentWillUnmount() {
    AppKeydownWatchers.getInstance().watchErrorDebugger(false);
    AppKeydownWatchers.getInstance().watchUserActivity(false);
    window.clearInterval(this.refreshTimer);
  }

  keepSessionAlive = () => {
    const { dispatch } = this.props;
    const watcher = AppKeydownWatchers.getInstance();
    if (watcher.data.userActivity) {
      dispatch(renewSessionRequest(null));
      watcher.restartUserActivity();
    }
  };

  designClass() {
    const { location } = this.props;

    if (location && location.query && location.query.design) {
      return 'design';
    }

    if (
      window
      && window.location
      && window.location.search
      && window.location.search.includes('design')
    ) {
      return 'design';
    }

    return '';
  }

  // Stop attempting to show the warning page for the rest of session once it has been checked
  // After data has been loaded, or once the user clicks to continue to the form if it has been shown
  onKickbackWarningCheck() {
    this.kickbackWarningHasBeenDisabled = true;
  }

  // Show the kickback warning if citizenship status has no value, all kickback data is expected to have it
  // Citizenship.Status is checked here so the actual internal check to decide if the form should be shown only
  // Occurs once the form data is actually loaded, otherwise it will always look empty
  showKickbackFailureWarning() {
    if (!this.kickbackWarningHasBeenDisabled && this.props.settings?.status === 'KICKBACK'
      && this.props.application?.Citizenship?.Status) {
      if (!this.props.application?.Citizenship?.Status?.CitizenshipStatus?.value) {
        return true;
      }
    }

    return false;
  }

  render() {
    const {
      formType, formSections, settings, children, apiVersion, clientVersion, showSurvey
    } = this.props;

    const formDetails = FormDetails.getInstance().getData();

    let formName = '';

    const displayCreditFreeze = shouldDisplayCreditFreeze(formType, formDetails.formVersion);

    switch (formDetails.formType) {
      case formTypes.SF852013:
        formName = formTypes[formType];
        break;
      case formTypes.SF85PS:
        formName = 'SF85P+S';
        break;
      case formTypes.PVQ_A:
        formName = 'PVQ-A';
        break;
      case formTypes.PVQ_A_B:
        formName = 'PVQ-A-B';
        break;
      case formTypes.PVQ_A_B_C:
        formName = 'PVQ-A-B-C';
        break;
      case formTypes.PVQ_A_B_D:
        formName = 'PVQ-A-B-D';
        break;
      default:
        formName = (formDetails.formVersion)
          ? formDetails.formType + formDetails.formVersion.slice(0, 4) : formTypes[formType];
        break;
    }

    const appClasses = classnames(this.designClass());

    const mobileNavigation = settings.mobileNavigation || false;

    const titleClasses = classnames('eapp-structure-right', 'eapp-title');

    const headingClasses = classnames('eapp-structure-wrap', 'eapp-header', {
      'mobile-navigation': mobileNavigation,
    });

    const mainClasses = classnames('eapp-structure-wrap', 'eapp-main', {
      'mobile-navigation': mobileNavigation,
    });

    const navigationClasses = classnames('eapp-structure-left', 'eapp-navigation', {
      'mobile-visible': mobileNavigation,
      'tablet-visible': mobileNavigation,
      'desktop-visible': mobileNavigation,
      'mobile-hidden': !mobileNavigation,
    });

    const coreClasses = classnames('eapp-structure-right', 'eapp-core', {
      'mobile-hidden': mobileNavigation,
      visible: !mobileNavigation,
    });

    /* eslint jsx-a11y/anchor-has-content: 0 */
    /* eslint jsx-a11y/anchor-is-valid: 0 */
    /* eslint no-script-url: 0 */

    return (
      <div className={appClasses}>
        <Introduction />
        <div id="scrollTo" />
        <a className="usa-skipnav" href="#main-content">
          {i18n.t('app.skip')}
        </a>
        <StickyHeader stickyClass="sticky-header">
          <div className="header-container">
            <div className="header">
              <header className="usa-header usa-header-basic" role="banner">
                <div className="usa-banner mobile-hidden">
                  <div className="usa-accordion">
                    <div className="usa-banner-header">
                      <div className="usa-grid usa-banner-inner">
                        <img src="/img/favicons/favicon-57.png" alt="U.S. flag" />
                        <p>{i18n.t('app.banner.title')}</p>
                        <button
                          type="button"
                          className="usa-accordion-button usa-banner-button"
                          aria-expanded="false"
                          aria-controls="gov-banner"
                        >
                          <span className="usa-banner-button-text">
                            {i18n.t('app.banner.button')}
                          </span>
                        </button>
                      </div>
                    </div>
                    <div
                      className="usa-banner-content usa-grid usa-accordion-content"
                      id="gov-banner"
                    >
                      <div className="usa-banner-guidance-gov usa-width-one-half">
                        <img
                          className="usa-banner-icon usa-media_block-img"
                          src="/img/icon-dot-gov.svg"
                          alt="Dot gov"
                        />
                        <div className="usa-media_block-body">
                          <p>
                            <strong>{i18n.t('app.banner.witty')}</strong>
                            <br />
                            {i18n.t('app.banner.extension')}
                          </p>
                        </div>
                      </div>
                      <div className="usa-banner-guidance-ssl usa-width-one-half">
                        <img
                          className="usa-banner-icon usa-media_block-img"
                          src="/img/icon-https.svg"
                          alt="SSL"
                        />
                        <div className="usa-media_block-body">
                          <p>{i18n.t('app.banner.ssl')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={headingClasses}>
                  <div className="eapp-structure-row">
                    <div className="eapp-structure-left eapp-logo" id="logo">
                      <NavigationToggle />
                      <div className="eapp-logo-container">
                        <img
                          className="eapp-logo-icon"
                          src="/img/dcsa-logo.png"
                          alt="National Background Investigation Services"
                        />
                        <span className="eapp-logo-text">{formName}</span>
                      </div>
                    </div>
                    <div className={titleClasses}>
                      <div className="eapp-logout">
                        {showSurvey === true && (
                          <Survey className="mobile-hidden" />
                        )}
                        {!this.submitted && (
                          <a
                            href="/instructions"
                            target="_blank"
                            className="instructions mobile-hidden"
                          >
                            {i18n.t('app.instructions')}
                          </a>
                        )}
                        {!this.submitted && (
                          <a
                            href="/help"
                            target="_blank"
                          >
                            {i18n.t('app.help')}
                          </a>
                        )}
                        <Logout />
                      </div>
                      {!mobileNavigation && <SectionTitle formType={formType} />}
                    </div>
                  </div>
                </div>
              </header>
              <div id="scrollToProgress" />
              <div className="usa-overlay" />
              <ProgressBar />
            </div>
          </div>
        </StickyHeader>
        {settings.status === 'KICKBACK' && <KickbackBanner />}
        {displayCreditFreeze && (
          <CreditFreezeBanner
            currentSubsectionKey={this.props?.subsectionKey}
          />
        )}
        {this.showKickbackFailureWarning() && (
          <KickbackFailureWarning
            onKickbackWarningDismiss={this.onKickbackWarningCheck}
          />
        )}
        <main className={mainClasses}>
          <div className="eapp-structure-row">
            <div className={navigationClasses}>
              <ScoreCard />
              <Navigation sections={formSections} />

              <a
                type="button"
                href="/instructions"
                target="_blank"
                className="instructions mobile-visible"
              >
                <span>{i18n.t('app.instructions')}</span>
              </a>
              {showSurvey === true && (
                <div
                  style={{
                    paddingLeft: '20px', marginTop: '-20px'
                  }}
                >
                  <Survey className="mobile-visible" />
                </div>
              )}
            </div>
            <div id="main-content" className={coreClasses}>
              <LinkButton
                classContent="eapp-section-focus"
                title="Main content. Please press TAB to go to the next question"
                ref={this.sectionFocusEl}
              />
              <Form {...this.props} />
              {children}
            </div>
          </div>
        </main>
        <div
          style={{
            position: 'absolute', top: '0px', right: '0px', width: '20px', height: '40px'
          }}
          title={`Client Version: ${clientVersion?.clientVersionText}\nAPI Version: ${apiVersion?.apiVersionText}\n`}
        >
          &nbsp;
        </div>
      </div>
    );
  }
}

/* eslint react/forbid-prop-types: 0 */
App.propTypes = {
  location: PropTypes.object.isRequired,
  formType: PropTypes.string.isRequired,
  formSections: PropTypes.array,
  settings: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  token: PropTypes.any,
  showSurvey: PropTypes.bool,
};

App.defaultProps = {
  formSections: [],
  token: null,
  showSurvey: false,
};

function mapStateToProps(state) {
  const { application, authentication } = state;
  const clientVersion = state?.api?.clientVersion;
  const apiVersion = state?.api?.apiVersion;

  const settings = application.Settings;
  const { authenticated, showSessionWarning, lastLogin } = authentication;

  let formSections = nestedFormSectionsSelector(state).concat(reviewSections);
  if (isPVQForm(settings?.formType)) {
    formSections = nestedFormSectionsSelector(state).concat(pvqReviewSections);
  }

  return {
    settings,
    formType: settings.formType,
    formSections,
    application,
    authenticated,
    showSessionWarning,
    lastLogin,
    clientVersion,
    apiVersion,
    ...getSectionKeys(state),
  };
}

export const internalTestingApp = {
  App,
};

// Wraps the App component with connect() which adds the dispatch()
// function to the props property for this component
export default withRouter(connect(mapStateToProps)(App));
